import React, { useState, Fragment, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { UrlSearchParams } from "types/enums";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { SCORE_THRESHOLD } from "../../TrackPageV3";
import ContentModal from "components/content-modal/ContentModal";
import { useInViews } from "utility/useInViews";
import { trackEvent } from "utility/mixpanel";

import styles from "./theme-modal.module.scss";
import { ItemScore } from "apps/user/learner/api/types";

interface ThemeModalPropsType {
  themeId: string;
  themeName: string;
  themeBlurb: string;
  items: ItemScore[];
  themeShorthand?: string;
  themeScore?: number | null;
  /** Stable band identity for analytics — themeLabel is localised display text. */
  themeLabelKey?: string | null;
  /** Stable domain identity. Never the display name, which is localised. */
  domainShorthand?: string;
}

const ThemeModal: React.FC<ThemeModalPropsType> = ({
  themeName,
  themeBlurb,
  items,
  themeShorthand,
  themeScore,
  themeLabelKey,
  domainShorthand,
}) => {
  const { ref, inView } = useInViews();

  const logImpression = useCallback(() => {
    trackEvent("Theme Detail Opened", {
      "Theme Shorthand": themeShorthand || null,
      "Theme Score": themeScore ?? null,
      "Theme Band": themeLabelKey || null,
      "Domain Shorthand": domainShorthand || null,
      "Item Count": items.length,
      Items: items.map((item) => item.shorthand),
    });
  }, [themeShorthand, themeScore, themeLabelKey, domainShorthand, items]);
  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);

  const handleClose = () => {
    const expandedItems = items.filter((item) =>
      expandedItemIds.includes(item.id)
    );
    trackEvent("Theme Detail Closed", {
      "Theme Shorthand": themeShorthand || null,
      "Items Expanded Count": expandedItems.length,
      "Expanded Items": expandedItems.map((item) => item.shorthand),
      "Domain Shorthand": domainShorthand || null,
    });
    const params = new URLSearchParams(location.search);
    params.delete(UrlSearchParams.Theme);
    const newSearch = params.toString();
    navigate(
      newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
      { replace: true }
    );
  };

  const toggleItem = (itemId: string) => {
    setExpandedItemIds((prev) => {
      const isCurrentlyExpanded = prev.includes(itemId);
      const item = items.find((item) => item.id === itemId);

      if (isCurrentlyExpanded) {
        trackEvent("Theme Item Collapsed", {
          "Item Shorthand": item?.shorthand || null,
          "Item Score": item?.value ?? null,
          "Item Band": item?.labelKey || null,
          "Theme Shorthand": themeShorthand || null,
          "Is Good Score": item ? item.value >= SCORE_THRESHOLD : false,
        });
        return prev.filter((id) => id !== itemId);
      } else {
        trackEvent("Theme Item Expanded", {
          "Item Shorthand": item?.shorthand || null,
          "Item Score": item?.value ?? null,
          "Item Band": item?.labelKey || null,
          "Theme Shorthand": themeShorthand || null,
          "Is Good Score": item ? item.value >= SCORE_THRESHOLD : false,
        });
        return [...prev, itemId];
      }
    });
  };

  return (
    <ContentModal onClose={handleClose} size="lg">
      <div className={styles["modal-container"]} ref={ref}>
        <div className={styles.header}>
          <p id="theme-modal-title" className={styles.title}>
            {themeName}
          </p>
          <p className={styles.description}>{themeBlurb}</p>
        </div>

        <div className={styles.content}>
          {items.map((item, index) => {
            const isExpanded = expandedItemIds.includes(item.id);
            const isAboveThreshold = item.value >= SCORE_THRESHOLD;

            return (
              <Fragment key={item.id}>
                <div className={styles["item-card"]}>
                  <button
                    className={styles["item-header"]}
                    onClick={() => toggleItem(item.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`item-content-${item.id}`}
                  >
                    <div className={styles["item-info"]}>
                      <p className={styles["item-name"]}>{item.name}</p>
                      <div className={styles["item-score"]}>
                        <span
                          className={clsx(styles["score-dot"], {
                            [styles["score-dot-above"]]: isAboveThreshold,
                            [styles["score-dot-below"]]: !isAboveThreshold,
                          })}
                        />
                        <span className={styles["score-value"]}>
                          {item.value}%
                        </span>
                        <span className={styles["score-label"]}>
                          {item.label}
                        </span>
                      </div>
                    </div>
                    <ChevronIcon
                      className={clsx(styles.chevron, {
                        [styles["chevron-expanded"]]: isExpanded,
                      })}
                    />
                  </button>

                  {isExpanded && (
                    <div
                      id={`item-content-${item.id}`}
                      className={styles["item-content"]}
                    >
                      <p className={styles["item-blurb"]}>{item.blurb}</p>
                    </div>
                  )}
                </div>
                {index < items.length - 1 && <div className={styles.divider} />}
              </Fragment>
            );
          })}
        </div>
      </div>
    </ContentModal>
  );
};

export default ThemeModal;
